import React, { useEffect, useState } from "react";
import ReactAudioPlayer from "react-audio-player";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { ApiService } from "../../../../../../../apis/ApiService";
import {
    PostLevelStep,
    PostSimpleTextData,
} from "../../../../../../../apis/ProjectInterfaces";
import { FileInput } from "../../../../../../../components/ui/input/fileInput/FileInput";
import {
    SelectInput,
    SelectOptionProps,
} from "../../../../../../../components/ui/input/selectInput/SelectInput";
import { TextInput } from "../../../../../../../components/ui/input/textInput/TextInput";
import { Loading } from "../../../../../../../components/ui/loading/Loading";
import {
    checkObjectEquality,
    getBase64,
} from "../../../../../../../utils/functions";
import { LoadingSaveInputDiscardButton } from "../../StepModal";

import styles from "./SimpleTextCreator.module.css";

let currentFile: any;
let currentSelectedImage: string;

export const SimpleTextCreator = (props: {
    step: PostLevelStep;
    mandatoryLanguages: string[];
    possibleLanguages: string[];
    onSend: (newStep: PostLevelStep) => Promise<boolean>;
    onChanges: (newChanges: boolean) => void;
}) => {
    const { projectId } = useParams<{ projectId: string }>();
    const { mandatoryLanguages, possibleLanguages, onSend, onChanges } = props;

    const [imageOptions, setImageOptions] = useState<string[]>([]);
    const [imageUploadResult, setImageUploadResult] = useState(<p></p>);
    const [imagePreview, setImagePreview] = useState<string>();

    const [uploaded, setUploaded] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [changes, setChanges] = useState(false);

    const { t } = useTranslation();

    const imageForm = useForm();
    const form = useForm({
        mode: "onBlur",
    });

    let { step } = props;

    const onDiscard = () => {
        form.reset();
        if (changes) {
            setChanges(false);
            onChanges(false);
        }
    };
    const onSubmit = form.handleSubmit(onSave);

    async function updateImageOptions() {
        try {
            await ApiService.getProjectCommonImagesNames(projectId).then(
                (res) => {
                    setImageOptions(res);
                }
            );
        } catch {
            setImageUploadResult(
                <p className="error">
                    {t("The image obtention service does not work")}
                </p>
            );
        }
    }

    function checkChanges() {
        formatTextCreatorResults(form.watch()).then((results: any) => {
            if (
                !checkObjectEquality(step.data, results) &&
                results !== undefined &&
                !changes
            ) {
                setChanges(true);
                onChanges(true);
            } else if (checkObjectEquality(step.data, results) && changes) {
                setChanges(false);
                onChanges(false);
            }
        });
    }

    async function onSave(data: any) {
        if (uploaded || uploading) return;
        setUploading(true);
        let newData = await formatTextCreatorResults(data);
        let newStep: PostLevelStep = {
            type: step.type,
            data: newData as any,
            id: step.id,
        };
        const success = await onSend(newStep);
        if (success) setUploaded(true);
        setUploading(false);
    }

    function getSelectOptions(options: string[]): SelectOptionProps[] {
        return options.map((val) => {
            return {
                display: val,
                value: val,
            };
        });
    }

    useEffect(() => {
        updateImageOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (step.type !== "SimpleText") return <> </>;

    if (imageForm.watch("image") && imageForm.watch("image") !== currentFile) {
        currentFile = imageForm.watch("image");

        let file = currentFile[0] as File;

        ApiService.postProjectCommonImage(file, projectId, file.name)
            .then((res) => {
                setImageUploadResult(<p>{res}</p>);
                updateImageOptions();
            })
            .catch((err) =>
                setImageUploadResult(<p className="error">{err}</p>)
            )
            .finally(() => imageForm.reset());
    }

    if (form.watch("imageSelect") !== currentSelectedImage) {
        currentSelectedImage = form.watch("imageSelect");
        if (currentSelectedImage !== "")
            ApiService.getProjectCommonImage(projectId, currentSelectedImage)
                .then((res) => setImagePreview(res))
                .catch(() => {});
    }

    checkChanges();

    return (
        <FormProvider {...form}>
            <form onSubmit={onSubmit}>
                <h2>{t("Text")}</h2>
                <div className={styles.textInputsContainer}>
                    {possibleLanguages.map((lang, ind) => (
                        <div
                            key={ind}
                            className={styles.contentTextInputContainer}
                        >
                            <label>{t(lang)}</label>
                            <TextInput
                                name={`${lang}Text`}
                                defaultValue={
                                    step.type === "SimpleText"
                                        ? step.data.text[lang]
                                        : ""
                                }
                                mandatory={mandatoryLanguages.includes(lang)}
                                multiline
                                rows={3}
                                key={ind}
                            />
                        </div>
                    ))}
                </div>
                <h2>{t("Image")}</h2>
                <div className={styles.imageGroupContainer}>
                    <div className={styles.imageGroupContainerFirstRow}>
                        <div className={styles.imageSelectContainer}>
                            <SelectInput
                                name="imageSelect"
                                options={getSelectOptions(imageOptions)}
                                box
                                defaultValue={step.data.imageName}
                            />
                        </div>

                        <FormProvider {...imageForm}>
                            <form className={styles.imageInputContainer}>
                                <FileInput
                                    height={50}
                                    inputProps={{
                                        name: "image",
                                        accept: "image/jpeg, image/png",
                                    }}
                                    button
                                    className={styles.imageInput}
                                    placeholder={t("Upload a file")}
                                />
                            </form>
                        </FormProvider>
                        <div className={styles.imageUploadResult}>
                            {imageUploadResult}
                        </div>
                    </div>
                    {imagePreview ? (
                        <img
                            src={imagePreview}
                            className={styles.imgPreview}
                            alt="Selected preview"
                        />
                    ) : currentSelectedImage !== "" && currentSelectedImage ? (
                        <Loading />
                    ) : undefined}
                </div>
                <h2>{t("Audio")}?</h2>
                <div className={styles.audiosContainer}>
                    {possibleLanguages.map((val, ind) => (
                        <div
                            key={ind}
                            className={styles.audioLanguageContainer}
                        >
                            <AudioInput lan={val} step={step} key={val} />
                        </div>
                    ))}
                </div>
                <LoadingSaveInputDiscardButton
                    onDiscard={onDiscard}
                    loadingVisible={uploading}
                    saveVisible={changes}
                />
            </form>
        </FormProvider>
    );
};

const AudioInput = (props: { step: PostLevelStep; lan: string }) => {
    const { t } = useTranslation();
    const { step, lan } = props;
    const methods = useFormContext();
    const [currentResult, setCurrentResult] = useState<any>();
    const [audioFile, setAudioFile] = useState<string>();
    const [loading, setLoading] = useState(false);
    const { projectId, levelId } =
        useParams<{
            projectId: string;
            levelId: string;
        }>();
    let fileInputName = lan + "FileAudio";
    let textInputName = lan;
    let filename = lan + ".mp3";
    useEffect(() => {
        if (step.type === "SimpleText") {
            if (step.id) {
                setLoading(true);
                ApiService.getStepFile(projectId, levelId, step.id, filename)
                    .then((res) => {
                        setAudioFile(res);
                    })
                    .catch(() => {})
                    .finally(() => setLoading(false));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (currentResult !== methods.watch(fileInputName)) {
        setCurrentResult(methods.watch(fileInputName));
        setAudioFile(URL.createObjectURL(methods.watch(fileInputName)[0]));
    }

    if (step.type !== "SimpleText") return <></>;

    function validateCheckbox(value: boolean) {
        if (value) {
            if (!methods.watch(fileInputName) && !audioFile) return false;
            else return true;
        }
        return true;
    }
    return (
        <>
            <div className={styles.audioLabel}>
                <input
                    ref={methods.register({ validate: validateCheckbox })}
                    type="checkbox"
                    defaultChecked={step.data.audio?.[lan]}
                    name={textInputName}
                />

                <label>{t(lan)}</label>
            </div>
            {loading ? (
                <Loading />
            ) : (
                <>
                    {audioFile ? (
                        <div className={styles.audioPlayer}>
                            <ReactAudioPlayer src={audioFile} controls />
                        </div>
                    ) : undefined}
                    <FileInput
                        inputProps={{
                            name: fileInputName,
                            accept: ".mp3",
                        }}
                        height={40}
                        button
                        placeholder={t("Upload a file")}
                        className={styles.fileInput}
                    />
                </>
            )}
            {methods.errors[textInputName] && (
                <p className="error" style={{ marginTop: 20 }}>
                    {t(
                        `You cannot check a language if you don't upload a file`
                    ) + ` (${t(lan)})`}
                </p>
            )}
        </>
    );
};

async function formatTextCreatorResults(results: { [key: string]: any }) {
    let data: PostSimpleTextData;
    data = {
        text: {},
        audio: {},
    };

    if (JSON.stringify(results) === "{}") return;

    for (const key in results) {
        if (results[key] instanceof File) return;
        if (key.search("Text") !== -1) {
            let lan = key.slice(0, key.search("Text"));
            if (results[lan + "Text"] !== "")
                data.text[lan] = results[lan + "Text"];
            data.audio![lan] = results[lan];
            if (results[lan + "FileAudio"]) {
                if (!data.audioBase64) data.audioBase64 = {};
                let base64 = await getBase64(results[lan + "FileAudio"][0]);
                data.audioBase64![lan] = base64;
            }
        }
    }
    data.imageName = results["imageSelect"];
    return data;
}
